import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Users, BarChart3, Target, RefreshCw, CheckCircle, Clock } from 'lucide-react';
import ExportMenu from '../../Common/ExportMenu';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { auth } from '../../../lib/firebase';
import { formatInTenantTz } from '../../../lib/datetime';
import { buildDailyTrendModel, buildStatusDistributionModel, buildIntentBarModel } from '../../../lib/chartModels';
import { tenantHeaders } from '../../../lib/tenantSession';

// Director Dashboard — wired to LIVE tenant analytics (no illustrative/mock data).
// Sources: GET /analytics/business/{brandId}?period=, /analytics/executive/{brandId},
// /ai-chat/analytics/chat-metrics. Metrics with no backend source are not shown
// (we never fabricate). All exports operate on the rendered live data.
const PERIODS = [
  { key: 'day', label: 'Today', api: '1d' },
  { key: 'week', label: 'This Week', api: '7d' },
  { key: 'month', label: 'This Month', api: '30d' },
  { key: 'quarter', label: 'This Quarter', api: '90d' },
  { key: 'year', label: 'This Year', api: '365d' },
];
const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444'];

const getToken = async () => {
  try { if (auth.currentUser) return await auth.currentUser.getIdToken(); } catch {}
  return localStorage.getItem('crm_access_token') || '';
};

const DirectorDashboard = () => {
  const { currentBusiness } = useBusiness();
  const { t } = useLanguage();
  const brandId = currentBusiness?.brandId;
  const base = process.env.REACT_APP_BACKEND_URL || '';

  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [biz, setBiz] = useState(null);
  const [chat, setChat] = useState(null);

  const load = useCallback(async () => {
    if (!brandId) { setLoading(false); return; }
    setLoading(true);
    const apiPeriod = (PERIODS.find((p) => p.key === period) || {}).api || '30d';
    try {
      const token = await getToken();
      const h = { Authorization: `Bearer ${token}`, ...tenantHeaders() };
      const [b, c] = await Promise.allSettled([
        fetch(`${base}/api/v1/analytics/business/${brandId}?period=${apiPeriod}`, { headers: h }),
        fetch(`${base}/api/v1/ai-chat/analytics/chat-metrics?business_id=${brandId}`, { headers: h }),
      ]);
      setBiz(b.status === 'fulfilled' && b.value.ok ? await b.value.json() : null);
      setChat(c.status === 'fulfilled' && c.value.ok ? await c.value.json() : null);
    } catch (_) {
      setBiz(null); setChat(null);
    } finally {
      setLoading(false);
    }
  }, [brandId, period, base]);

  useEffect(() => { load(); }, [load]);

  const resolutionRate = biz && biz.total_tickets ? Math.round((biz.resolved_tickets / biz.total_tickets) * 100) : null;
  const dailyTrend = buildDailyTrendModel(biz?.daily_metrics);
  const statusDistribution = buildStatusDistributionModel(biz);
  const intentData = buildIntentBarModel(chat?.intent_distribution);
  const intentTotal = intentData.reduce((s, i) => s + i.count, 0);

  if (!brandId) {
    return (
      <div className="flex-1 p-6"><Card><CardContent className="py-16 text-center text-gray-600">
        {t('pg.director.workspaceNotLinked')}
      </CardContent></Card></div>
    );
  }

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('pg.director.title')}</h1>
          <p className="text-gray-600">{t('pg.director.livePerf')} {currentBusiness?.businessName || brandId}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => <SelectItem key={p.key} value={p.key}>{t(`pcb.director.period${p.key.charAt(0).toUpperCase()}${p.key.slice(1)}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />{t('common.refresh')}
          </Button>
        </div>
      </div>

      {!loading && !biz && (
        <Card className="mb-6"><CardContent className="py-10 text-center text-gray-600">
          {t('pg.director.liveUnavailable')}
          <div className="mt-3"><Button variant="outline" onClick={load}>{t('common.retry')}</Button></div>
        </CardContent></Card>
      )}

      <Tabs defaultValue="productivity" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="productivity">{t('pg.director.teamProductivity')}</TabsTrigger>
          <TabsTrigger value="workload">{t('pg.director.workloadDistribution')}</TabsTrigger>
          <TabsTrigger value="kpis">{t('pg.director.departmentKpis')}</TabsTrigger>
        </TabsList>

        {/* Team Productivity — live */}
        <TabsContent value="productivity" className="space-y-6">
          <div className="flex justify-end">
            <ExportMenu data={dailyTrend} element="#dir-productivity" filename="director-productivity" title={t('pg.director.teamProductivity')} />
          </div>
          <div className="grid md:grid-cols-3 gap-6" id="dir-productivity">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Target className="h-5 w-5" />{t('pg.director.resolutionRate')}</CardTitle></CardHeader>
              <CardContent><p className="text-3xl font-bold text-green-600">{resolutionRate != null ? `${resolutionRate}%` : '—'}</p>
                <p className="text-sm text-gray-500">{t('pcb.director.ofTickets', { resolved: biz?.resolved_tickets ?? 0, total: biz?.total_tickets ?? 0 })}</p></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>{t('pg.director.ticketsResolved')}</CardTitle></CardHeader>
              <CardContent><p className="text-3xl font-bold text-blue-600">{biz?.resolved_tickets ?? '—'}</p>
                <p className="text-sm text-gray-500">{t('pg.director.thisPeriod')}</p></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>{t('pg.director.avgFirstResponse')}</CardTitle></CardHeader>
              <CardContent><p className="text-3xl font-bold text-purple-600">{biz ? `${biz.avg_first_response_time}m` : '—'}</p>
                <p className="text-sm text-gray-500">{t('pg.director.avgResolution')} {biz ? `${biz.avg_resolution_time}h` : '—'}</p></CardContent>
            </Card>
          </div>
          <Card>
            <CardHeader><CardTitle>{t('pg.director.dailyResVsEsc')}</CardTitle></CardHeader>
            <CardContent>
              {dailyTrend.length === 0 ? (
                <div className="text-center py-16 text-gray-500 text-sm">{t('pcb.director.noTicketActivity')}</div>
              ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dailyTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(d) => formatInTenantTz(d, { month: 'short', day: 'numeric' })} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Resolved" fill="#10b981" />
                  <Bar dataKey="Escalated" fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Workload Distribution — live */}
        <TabsContent value="workload" className="space-y-6">
          <div className="flex justify-end">
            <ExportMenu data={intentData} element="#dir-workload" filename="director-workload" title={t('pg.director.workloadDistribution')} />
          </div>
          <div className="grid md:grid-cols-2 gap-6" id="dir-workload">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5" />{t('pcb.director.ticketStatusDist')}</CardTitle></CardHeader>
              <CardContent>
                {statusDistribution.length === 0 ? (
                  <div className="text-center py-16 text-gray-500 text-sm">{t('pcb.director.noTicketData')}</div>
                ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={statusDistribution} cx="50%" cy="50%" labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`} outerRadius={90} dataKey="value">
                      {statusDistribution.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" />{t('pcb.director.conversationIntents')}</CardTitle></CardHeader>
              <CardContent>
                {intentData.length === 0 ? (
                  <div className="text-center py-16 text-gray-500 text-sm">{t('pcb.director.noIntentData')}</div>
                ) : (
                <div className="space-y-3">
                  {intentData.slice(0, 8).map((it, i) => {
                    const pct = intentTotal ? Math.round((it.count / intentTotal) * 100) : 0;
                    return (
                    <div key={it.name} className="space-y-1">
                      <div className="flex justify-between text-sm"><span className="font-medium capitalize">{it.name}</span><span>{it.count} ({pct}%)</span></div>
                      <div className="w-full bg-gray-200 rounded-full h-2"><div className="h-2 rounded-full" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} /></div>
                    </div>
                    );
                  })}
                </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Department KPIs — live */}
        <TabsContent value="kpis" className="space-y-6">
          <div className="flex justify-end">
            <ExportMenu element="#dir-kpis" filename="director-kpis" title={t('pg.director.departmentKpis')} />
          </div>
          <div className="grid md:grid-cols-3 gap-6" id="dir-kpis">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Target className="h-5 w-5" />{t('pcb.director.customerSatisfaction')}</CardTitle></CardHeader>
              <CardContent><p className="text-3xl font-bold text-green-600">{biz?.customer_satisfaction ? `${biz.customer_satisfaction}/5` : '—'}</p>
                <p className="text-sm text-gray-500">{biz?.csat_response_count ?? 0} {t('pcb.director.responses')}</p></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><CheckCircle className="h-5 w-5" />{t('pcb.director.aiAutoResolution')}</CardTitle></CardHeader>
              <CardContent><p className="text-3xl font-bold text-blue-600">{biz ? `${biz.ai_resolution_rate}%` : '—'}</p>
                <p className="text-sm text-gray-500">{t('pcb.director.resolvedWithoutStaff')}</p></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Clock className="h-5 w-5" />{t('pcb.director.escalationRate')}</CardTitle></CardHeader>
              <CardContent><p className="text-3xl font-bold text-orange-600">{biz ? `${biz.escalation_rate}%` : '—'}</p>
                <p className="text-sm text-gray-500">{t('pcb.director.callbackRate')} {biz ? `${biz.callback_rate}%` : '—'}</p></CardContent>
            </Card>
          </div>
          <Card>
            <CardHeader><CardTitle>{t('pcb.director.kpisTitle')}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center"><span>{t('pcb.director.avgResolutionTime')}</span><span className="font-bold">{biz ? `${biz.avg_resolution_time} h` : '—'}</span></div>
              <div className="flex justify-between items-center"><span>{t('pcb.director.avgFirstResponseFull')}</span><span className="font-bold text-green-600">{biz ? `${biz.avg_first_response_time} min` : '—'}</span></div>
              <div className="flex justify-between items-center"><span>{t('pcb.director.reopenedTickets')}</span><span className="font-bold text-orange-600">{biz?.reopened_tickets ?? '—'}</span></div>
              <div className="flex justify-between items-center"><span>{t('pcb.director.reopenedConversations')}</span><span className="font-bold text-purple-600">{biz?.reopened_conversations ?? '—'}</span></div>
              <div className="flex justify-between items-center"><span>{t('pcb.director.openaiUsageRate')}</span><span className="font-bold text-blue-600">{biz ? `${biz.openai_usage_rate}%` : '—'}</span></div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DirectorDashboard;
